import React from 'react'
import styled from 'styled-components'
import { useWallet } from '../../components/WalletProvider'
import { useDemo } from '../../components/DemoProvider'
import ProjectRequestForm from '../../components/ProjectRequestForm'

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 0%,
      rgba(59, 130, 246, 0.1) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto 2rem auto;
`

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: 1.125rem;
  max-width: 600px;
  line-height: 1.6;
`

const WalletRequired = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
  }

  h2 {
    font-size: 1.5rem;
    color: var(--color-text);
    margin-bottom: 1rem;
  }

  p {
    color: var(--color-text-secondary);
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`

const ConnectButton = styled.button`
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &::before {
      left: 100%;
    }
  }
`

const WalletInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 2rem;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.$demo ? 'var(--color-primary)' : 'var(--color-secondary)'};
  }

  span {
    font-size: 0.875rem;
    color: var(--color-text-secondary);
  }
`

function RequestProject() {
  const { wallet, openModal } = useWallet()
  const { isDemoMode } = useDemo()

  const formatAddress = (address) => {
    if (!address) return ''
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  if (!wallet.isConnected && !isDemoMode) {
    return (
      <Container>
        <WalletRequired>
          <h2>Connect Your Wallet</h2>
          <p>
            Please connect your Solana wallet to create a new project. Your project
            configuration and data will be securely tied to your wallet address.
          </p>
          <ConnectButton onClick={openModal}>
            Connect Wallet
          </ConnectButton>
        </WalletRequired>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <WalletInfo $demo={isDemoMode}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {isDemoMode ? (
              <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
            ) : (
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            )}
          </svg>
          <span>
            {isDemoMode ? 'Demo Mode' : `Connected: ${formatAddress(wallet.publicKey)}`}
          </span>
        </WalletInfo>
        <Title>Create a Project</Title>
        <Subtitle>
          Configure and deploy AI agents for your project needs. Our agents will
          collaborate to deliver your project efficiently and effectively.
        </Subtitle>
      </Header>
      <ProjectRequestForm />
    </Container>
  )
}

export default RequestProject
